import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GuestPrecriptionInitialTab, {
  GuestPrecriptionInitialTabAction
} from 'components/Prescription/GuestPrescriptionInitialTab';
import PrescriptionForm from 'components/Prescription/PrescriptionForm';
import PrescriptionUploadForm from 'components/Prescription/PrescriptionUploadForm';
import { openAuth } from 'redux/features/auth/authSlice';
import {
  setOpenPrescriptionGuestForm,
  setOpenUploadPrescriptionGuestForm,
  setSelectEditedPrescription
} from '../../redux/features/prescription/prescriptionSlice';
import { RootState } from '../../redux/store';
import Drawer from '../Drawer/Drawer';
import DrawerNavigation from './UIDrawerNavigation';
import { useGlassesStore } from 'zus/glassesStore';

export default function GuestPrescription() {
  const purpose = useGlassesStore((state) => state.purpose)

  const isMultifocal = (purpose == 'multifocal' || purpose == 'bifocal');

  const openPrescriptionGuestForm = useSelector(
    (state: RootState) => state.prescriptionEvent.openPrescriptionGuestForm
  );

  const selectEditedPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectEditedPrescription
  );
  const [visibleStep, setVisibleStep] = useState<
    'SELECT_ACTION' | 'MULTIFOCAL_UPLOAD' | 'PRESCRIPTION_FORM'
  >('SELECT_ACTION');

  const dispatch = useDispatch();

  const [uploadStep, setUploadStep] = useState<'UPLOAD' | 'FORM'>('UPLOAD');

  const actionClickHandler = (action: GuestPrecriptionInitialTabAction) => {
    if (action === 'UPLOAD_PRESCRIPTION') {
      setVisibleStep('MULTIFOCAL_UPLOAD');
      return;
    }
    if (action === 'ENTER_PRESCRIPTION') {
      setVisibleStep('PRESCRIPTION_FORM');
    }
    if (action === 'LOGIN') {
      dispatch(openAuth());
      dispatch(setOpenPrescriptionGuestForm(false));
      dispatch(setSelectEditedPrescription(null));
      dispatch(setOpenUploadPrescriptionGuestForm(false));
    }
  };

  useEffect(() => {
    if (selectEditedPrescription && openPrescriptionGuestForm) {
      setVisibleStep('PRESCRIPTION_FORM')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectEditedPrescription, openPrescriptionGuestForm]);

  const closeDrawer = () => {
    dispatch(setOpenPrescriptionGuestForm(false));
    dispatch(setOpenUploadPrescriptionGuestForm(false));
  };

  const navigateBackHandler = () => {
    setVisibleStep('SELECT_ACTION');
  };

  const isNavigateBackHidden = useMemo(
    () => visibleStep === 'SELECT_ACTION',
    [visibleStep]
  );

  const navigationTitle = useMemo(() => {
    if (visibleStep === 'MULTIFOCAL_UPLOAD' && uploadStep === 'FORM')
      return 'Enter Your Prescription';
    if (visibleStep === 'MULTIFOCAL_UPLOAD' && uploadStep === 'UPLOAD')
      return 'Upload Your Prescription';
    if (visibleStep === 'PRESCRIPTION_FORM') return 'Enter Your Prescription';

    return 'Your Prescription';
  }, [uploadStep, visibleStep]);

  if (!openPrescriptionGuestForm) return null

  return (
    <Drawer
      position="right"
      open={openPrescriptionGuestForm}
      clickable
      onCallEndAnimate={() => {
        dispatch(setOpenPrescriptionGuestForm(false));
        dispatch(setSelectEditedPrescription(null));
        dispatch(setOpenUploadPrescriptionGuestForm(false));
      }}
      className="h-full w-[680px] overflow-y-auto bg-white mdx:w-full"
      onClose={() => {
        dispatch(setOpenPrescriptionGuestForm(false));
        dispatch(setOpenUploadPrescriptionGuestForm(false));
      }}>
      <div className="px-[24px] py-[12px]">
        <DrawerNavigation
          title={navigationTitle}
          subTitle={purpose!}
          hideBack={isNavigateBackHidden}
          onNavigateBack={navigateBackHandler}
          onClose={closeDrawer}
        />
      </div>
      <div className="relative flex p-9 mdx:p-5">
        {visibleStep === 'SELECT_ACTION' && (
          <GuestPrecriptionInitialTab
            isMultifocal={isMultifocal}
            actionClickHandler={actionClickHandler}
          />
        )}
        {visibleStep === 'MULTIFOCAL_UPLOAD' && (
          <div className="h-full w-full">
            <PrescriptionUploadForm onVisibleStepChanged={setUploadStep} />
          </div>
        )}
        {visibleStep === 'PRESCRIPTION_FORM' && (
          <div className="w-full">
            <PrescriptionForm />
          </div>
        )}
      </div>
    </Drawer>
  );
}
